import {
	TransferCounterpartPreviewRes,
	AutoReorderTransactionDto,
	BulkDeleteTransactionsDto,
	BulkUpdateTransactionDto,
	BulkUpdateTransactionsRes,
	BulkUploadTransactionsDto,
	CreateOneSchemaSessionSuccessRes,
	CreateOneSchemaTransactionsSessionDto,
	CreateInseparableRedemptionDto,
	CreatePairedIssueDto,
	CreateTransactionDto,
	DeleteTransactionDto,
	DownloadTransactionsDto,
	GetBulkUploadStatusRes,
	GetPairTransactionDto,
	GetTransactionsRes,
	InseparableRedemptionGroupRes,
	InseparableRedemptionPreviewRes,
	PairTransactionDto,
	PairTransactionRes,
	ReorderTransactionDto,
	SplitTransactionDto,
	TransactionListQueryDto,
	TransactionTct,
	TransactionWithSecurityTct,
	TransactionWithSecurityUnitDenominatorTct,
	UpdateInseparableRedemptionDto,
	UpdateTransactionDto,
} from '@dealstack/shared/lib/generated';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';

export const getTransactionsList = async (
	args: TransactionListQueryDto
): Promise<GetTransactionsRes | undefined> => {
	return makeRequest<ApiV1.Response<GetTransactionsRes>>({
		endpoint: API_PATHS.CAP_TRANSACTION_LIST_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((response) => response.payload);
};

export const getTransactionDetails = async (getTransactionDetailsDto: {
	id: string;
}): Promise<TransactionWithSecurityTct> => {
	const endpoint = formatUnicorn(API_PATHS.CAP_TRANSACTION_TRANSACTION_DETAILS_POST, {});
	return makeRequest<ApiV1.Response<TransactionWithSecurityTct>>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(getTransactionDetailsDto),
	}).then((response) => response.payload as TransactionWithSecurityTct);
};

export const reorderTransactionAfterOther = async (body: ReorderTransactionDto) => {
	return makeRequest<ApiV1.Response<TransactionTct>>({
		endpoint: API_PATHS.CAP_TRANSACTION_REORDER_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const downloadTransactionsExcel = async (args: DownloadTransactionsDto): Promise<Blob> => {
	return makeRequest<Blob>({
		endpoint: API_PATHS.CAP_TRANSACTION_DOWNLOAD_EXCEL_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

// CAP-1975: alias for the generated create payload, re-exported so controllers can type the
// request without colliding with the zod-inferred `CreateTransactionDto` used for the form.
export type CreateTransactionRequest = CreateTransactionDto;

export const createTransaction = async (args: CreateTransactionDto) => {
	return makeRequest<ApiV1.Response<TransactionTct>>({
		endpoint: API_PATHS.CAP_TRANSACTION_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

// CAP-1975: atomic nominee paired issue — the backend mints the "Both" title to the nominee and
// transfers the beneficial rail to the buyer on the same serials inside one transaction, replacing
// the previous best-effort two-call frontend sequence. Returns both created transactions.
export const createPairedIssue = async (args: CreatePairedIssueDto): Promise<TransactionTct[]> => {
	return makeRequest<ApiV1.Response<TransactionTct[]>>({
		endpoint: API_PATHS.CAP_TRANSACTION_PAIRED_ISSUE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload ?? []);
};

export const previewInseparableRedemption = async (
	args: CreateInseparableRedemptionDto
): Promise<InseparableRedemptionPreviewRes | undefined> => {
	return makeRequest<ApiV1.Response<InseparableRedemptionPreviewRes>>({
		endpoint: API_PATHS.CAP_TRANSACTION_INSEPARABLE_REDEMPTION_PREVIEW_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const previewTransferCounterpart = async (
	args: CreateTransactionRequest
): Promise<TransferCounterpartPreviewRes | undefined> => {
	return makeRequest<ApiV1.Response<TransferCounterpartPreviewRes>>({
		endpoint: API_PATHS.CAP_TRANSACTION_TRANSFER_COUNTERPART_PREVIEW_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const createInseparableRedemption = async (
	args: CreateInseparableRedemptionDto
): Promise<TransactionTct[]> => {
	return makeRequest<ApiV1.Response<TransactionTct[]>>({
		endpoint: API_PATHS.CAP_TRANSACTION_INSEPARABLE_REDEMPTION_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload ?? []);
};

export const getInseparableRedemptionGroup = async (
	redemptionGroupId: string
): Promise<InseparableRedemptionGroupRes | undefined> => {
	return makeRequest<ApiV1.Response<InseparableRedemptionGroupRes>>({
		endpoint: formatUnicorn(
			API_PATHS.CAP_TRANSACTION_INSEPARABLE_REDEMPTION_REDEMPTIONGROUPID_GET,
			{ redemptionGroupId }
		),
		method: HTTP_METHOD.GET,
	}).then((r) => r.payload);
};

export const updateInseparableRedemption = async (
	args: UpdateInseparableRedemptionDto
): Promise<InseparableRedemptionGroupRes | undefined> => {
	return makeRequest<ApiV1.Response<InseparableRedemptionGroupRes>>({
		endpoint: API_PATHS.CAP_TRANSACTION_INSEPARABLE_REDEMPTION_PATCH,
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const updateTransaction = async (args: UpdateTransactionDto) => {
	return await makeRequest<ApiV1.Response<TransactionTct>>({
		endpoint: API_PATHS.CAP_TRANSACTION_UPDATE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const restoreTransactionRequest = async ({ id }: { id: string }) => {
	return await makeRequest<ApiV1.Response<TransactionTct>>({
		endpoint: API_PATHS.CAP_TRANSACTION_RESTORE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ id }),
	}).then((r) => r.payload);
};

export const splitTransaction = async (args: SplitTransactionDto) => {
	return await makeRequest<ApiV1.Response<TransactionTct>>({
		endpoint: API_PATHS.CAP_TRANSACTION_SPLIT_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

// CAP-2011 "Update interest transferred": split a Both-titled transaction on a nominee security
// into its beneficial-interest and legal-title legs (beneficial keeps the full consideration and
// the attached records; legal is stamped 0).
export const splitTransactionInterest = async (args: { id: string }) => {
	return await makeRequest<ApiV1.Response<TransactionTct>>({
		endpoint: API_PATHS.CAP_TRANSACTION_SPLIT_INTEREST_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const getPairTransaction = async (args: GetPairTransactionDto) => {
	return await makeRequest<ApiV1.Response<TransactionWithSecurityUnitDenominatorTct[]>>({
		endpoint: API_PATHS.CAP_TRANSACTION_GET_PAIR_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const pairTransaction = async (args: PairTransactionDto) => {
	return await makeRequest<ApiV1.Response<PairTransactionRes>>({
		endpoint: API_PATHS.CAP_TRANSACTION_PAIR_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const bulkUpdateTransactions = async (args: BulkUpdateTransactionDto) => {
	return await makeRequest<ApiV1.Response<BulkUpdateTransactionsRes>>({
		endpoint: API_PATHS.CAP_TRANSACTION_BULK_UPDATE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const bulkRestoreTransactionsRequest = async (args: BulkUpdateTransactionDto) => {
	return await makeRequest<ApiV1.Response<BulkUpdateTransactionsRes>>({
		endpoint: API_PATHS.CAP_TRANSACTION_BULK_RESTORE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const deleteTransaction = async (args: DeleteTransactionDto) => {
	return await makeRequest<ApiV1.Response<TransactionTct>>({
		endpoint: API_PATHS.CAP_TRANSACTION_DELETE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const bulkUploadTransactions = async (args: BulkUploadTransactionsDto) => {
	return await makeRequest<ApiV1.Response<{ createdEntitiesCount: number }>>({
		endpoint: API_PATHS.CAP_TRANSACTION_BULK_UPLOAD_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const bulkDeleteTransactions = async (args: BulkDeleteTransactionsDto) => {
	return await makeRequest<ApiV1.Response<BulkUpdateTransactionsRes>>({
		endpoint: API_PATHS.CAP_TRANSACTION_BULK_DELETE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const createOneSchemaTransactionsSession = async (
	args: CreateOneSchemaTransactionsSessionDto
) => {
	return await makeRequest<ApiV1.Response<CreateOneSchemaSessionSuccessRes>>({
		endpoint: API_PATHS.ONE_SCHEMA_TRANSACTIONS_CREATE_ONE_SCHEMA_SESSION_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const getBulkUploadStatus = async (endpoint: string, signal?: AbortSignal) => {
	return makeRequest<ApiV1.Response<GetBulkUploadStatusRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	).then((r) => r.payload);
};

export const getBulkUploadStatusBySessionId = async (
	sessionId: string,
	signal?: AbortSignal
): Promise<GetBulkUploadStatusRes | undefined> => {
	const endpoint = formatUnicorn(API_PATHS.CAP_TRANSACTION_BULK_UPLOAD_SESSIONID_STATUS_GET, {
		sessionId,
	});
	return makeRequest<ApiV1.Response<GetBulkUploadStatusRes>>(
		{ endpoint, method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	).then((r) => r.payload);
};

export const autoReorderTransactions = async (body: AutoReorderTransactionDto) => {
	return makeRequest<ApiV1.Response<TransactionTct[]>>({
		endpoint: API_PATHS.CAP_TRANSACTION_AUTO_REORDER_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};
